import React, { useEffect } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineV2"
import { useRoutingFlow } from "../../shared/RoutingFlowContainer"
import { FormattedMessage, useIntl } from "react-intl"
import { Box, Button, ForwardIcon, Typography, Link } from "@carrotfertility/carotene-core"
import { Link as RouterLink, useHistory } from "react-router-dom"
import { RoutingFlowTitle } from "#/components/carrot-plans/shared"
// eslint-disable-next-line no-restricted-imports
import { ButtonProps, Container } from "@carrotfertility/carotene" // please do not import from @carrotfertility/carotene after eslint rule added on 3/20/25
import { CenteredLoadingIndicator } from "#/components/views/molecules/Molecules"
import { useShouldShowPreservationMedicalRecordsConsent } from "./useShouldShowPreservationMedicalRecordsConsent"
import { useCheckRoutingInfoQueryCache } from "#/components/carrot-plans/shared/useQueryRoutingInfo"
import { useCurrentUser } from "#/components/context/user/UserContext"
import { CarrotPlansPaths, Paths } from "#/utils/Paths"
import getCurrentRoutingFlowPath from "#/components/carrot-plans/shared/getRoutingFlowPath"
import useGetMemberJourneyData from "#/hooks/useGetMemberJourneyData"
import { autoText } from "#/services/locale/bidirectional-text"

export default function CarrotPlanOnTheWayStep(): JSX.Element {
  const intl = useIntl()
  const currentUser = useCurrentUser()
  const memberName = currentUser.preferredName ? currentUser.preferredName : currentUser.firstName
  const { send } = useStateMachine()
  const currentRoutingFlowPath = getCurrentRoutingFlowPath()
  const { setShowProgressTracker, setHideCloseButton, setDisableButtons } = useRoutingFlow()
  const routingData = useCheckRoutingInfoQueryCache()
  const memberJourneyQuery = useGetMemberJourneyData()
  const journeyData = memberJourneyQuery.data?.currentMemberJourney.withInProgressRouting
  const { shouldShowPreservationMedicalRecordsConsent, isLoadingShouldShowPreservationMedicalRecordsConsent } =
    useShouldShowPreservationMedicalRecordsConsent({ routingData, journeyData })

  useEffect(() => {
    setShowProgressTracker(false)
    if (isLoadingShouldShowPreservationMedicalRecordsConsent || memberJourneyQuery.isLoading) {
      setDisableButtons(true)
    } else {
      setHideCloseButton(true)
    }
  }, [
    isLoadingShouldShowPreservationMedicalRecordsConsent,
    memberJourneyQuery.isLoading,
    setDisableButtons,
    setHideCloseButton,
    setShowProgressTracker
  ])

  const isSensitiveScenario = routingData?.isStillInPrevJourneyStage === "SENSITIVE_SCENARIO"

  const headerCopy = isSensitiveScenario
    ? intl.formatMessage(
        { defaultMessage: "Thanks, {memberName}. We’re here for you." },
        { memberName: autoText(memberName) }
      )
    : currentRoutingFlowPath === CarrotPlansPaths.ENROLLMENT
    ? intl.formatMessage({ defaultMessage: "Your Carrot Plan is on its way" })
    : intl.formatMessage({ defaultMessage: "Thanks, {memberName}." }, { memberName: autoText(memberName) })

  if (isLoadingShouldShowPreservationMedicalRecordsConsent) {
    return <LoadingIndicator />
  }

  const renderButton = () => {
    if (currentRoutingFlowPath === CarrotPlansPaths.CHECK_IN_EXPERIENCE) {
      return <GoToCarrotHomeButton />
    } else if (shouldShowPreservationMedicalRecordsConsent) {
      return <MedicalRecordsConsentButton onClick={() => send("PRESERVATION_MEDICAL_RECORDS_CONSENT")} />
    } else {
      return <GoToCarrotHomeButton />
    }
  }

  return (
    <>
      <RoutingFlowTitle title={headerCopy} />
      <HeaderContent copy={headerCopy} />
      <MainContent sensitiveScenarioSupport={routingData?.sensitiveScenarioSupport || ""} />
      {renderButton()}
    </>
  )
}

const LoadingIndicator = (): JSX.Element => (
  <>
    <Container padding="large" stack="giant">
      <CenteredLoadingIndicator data-testid="carrot-plan-loader" />
    </Container>
  </>
)

const HeaderContent = ({ copy }: { copy: string }): JSX.Element => (
  <>
    <Box display="flex" justifyContent="center" marginBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
      <Box
        component="img"
        src={`/images/illo-underwater-dashboard.png`}
        srcSet={`/images/illo-underwater-dashboard-2x.png`}
        alt=""
        marginBottom={(theme) => theme.spacing(theme.tokens.spacing.md)}
        height={228}
        width={300}
      />
    </Box>
    <Typography
      color={(theme) => theme.palette.text.primary}
      variant="h3"
      component="h1"
      marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xs)}
    >
      {copy}
    </Typography>
  </>
)

const MainContent = ({ sensitiveScenarioSupport }: { sensitiveScenarioSupport: string }): JSX.Element => {
  const currentRoutingFlowPath = getCurrentRoutingFlowPath()

  return (
    <Typography marginBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
      {currentRoutingFlowPath === CarrotPlansPaths.ENROLLMENT ? (
        <FormattedMessage defaultMessage="Thank you for telling us about your journey and how we can help. We'll start working on your personalized Carrot Plan." />
      ) : sensitiveScenarioSupport === "NO_CARROT_PLAN_NEEDED" ? (
        <FormattedMessage
          defaultMessage="You won’t see any suggestions for now. You can also opt out of communications from Carrot in <accountSettingsLink>Account settings</accountSettingsLink>. {br}{br} If and when you’re ready for a new Carrot Plan, simply update your journey or reach out to your Carrot Companion."
          values={{
            br: <br />,
            accountSettingsLink: (linkContent) => (
              <Link to={Paths.ACCOUNT} component={RouterLink} color={"inherit"}>
                {linkContent}
              </Link>
            )
          }}
        />
      ) : (
        <FormattedMessage defaultMessage="Your Care Team will review your answers and add new suggestions to your Carrot Plan. We’ll notify you by email when your new plan is ready." />
      )}
    </Typography>
  )
}

const MedicalRecordsConsentButton = ({ onClick }: { onClick: ButtonProps["onClick"] }): JSX.Element => {
  return (
    <Box paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xl)} textAlign="end">
      <Button
        data-testid="next-button"
        size="medium"
        variant="outlined"
        color="secondary"
        endIcon={<ForwardIcon />}
        onClick={onClick}
      >
        <FormattedMessage defaultMessage="Next" />
      </Button>
    </Box>
  )
}

const GoToCarrotHomeButton = (): JSX.Element => {
  const history = useHistory()

  return (
    <>
      <Box marginBottom={(theme) => theme.spacing(theme.tokens.spacing.xxxl)} textAlign="center">
        <Button data-testid="go-to-carrot-home-button" onClick={() => history.push("/")}>
          <FormattedMessage defaultMessage="Go to Carrot home" />
        </Button>
      </Box>
    </>
  )
}
